/**
 * Google Analytics 4 Configuration
 * Provides tracking for user behavior, conversions, and performance metrics
 */

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

// Check if GA is available
const isGAAvailable = (): boolean => {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
};

// Session ID for page view tracking
let sessionId: string | null = null;

const getSessionId = (): string => {
  if (!sessionId) {
    sessionId = sessionStorage.getItem('vitly_session_id');
    if (!sessionId) {
      sessionId = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
      sessionStorage.setItem('vitly_session_id', sessionId);
    }
  }
  return sessionId;
};

const getDeviceType = (): 'mobile' | 'tablet' | 'desktop' => {
  if (typeof window === 'undefined') return 'desktop';
  const width = window.innerWidth;
  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
};

// Last page tracking for referrer
let lastPagePath: string | null = null;
let pageEnterTime: number = Date.now();

// Get GA Measurement ID from environment
const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;

// Get API URL for backend tracking
const API_URL = import.meta.env.VITE_API_URL || '';

/**
 * Get browser name from user agent
 */
const getBrowser = (): string => {
  if (typeof navigator === 'undefined') return 'unknown';
  const ua = navigator.userAgent;
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Chrome') && !ua.includes('Edg')) return 'Chrome';
  if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Safari';
  if (ua.includes('Edg')) return 'Edge';
  if (ua.includes('Opera') || ua.includes('OPR')) return 'Opera';
  return 'Other';
};

/**
 * Send page view to backend for analytics tracking
 */
const sendPageViewToBackend = async (
  path: string,
  pageTitle?: string
): Promise<void> => {
  if (!API_URL) return;

  const now = Date.now();
  const timeOnPage = lastPagePath ? Math.round((now - pageEnterTime) / 1000) : 0;

  try {
    await fetch(`${API_URL}/v1/analytics/page-view`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        path,
        pageTitle,
        referrerPath: lastPagePath || undefined,
        timeOnPage,
        sessionId: getSessionId(),
        deviceType: getDeviceType(),
        browser: getBrowser(),
      }),
    });
  } catch (error) {
    // Silently fail - analytics should never break the app
    console.debug('[Analytics] Backend page view failed:', error);
  }

  // Update tracking state
  lastPagePath = path;
  pageEnterTime = now;
};

/**
 * Initialize Google Analytics 4
 */
export const initializeAnalytics = (): void => {
  if (!GA_MEASUREMENT_ID) {
    console.log('[Analytics] GA_MEASUREMENT_ID not configured. Analytics disabled.');
    return;
  }

  console.log('[Analytics] Google Analytics 4 initialized with ID:', GA_MEASUREMENT_ID);
};

/**
 * Send event to Google Analytics
 */
const sendGAEvent = (eventName: string, parameters?: Record<string, any>): void => {
  if (!isGAAvailable()) {
    console.log('[Analytics] GA not available:', eventName, parameters);
    return;
  }

  try {
    window.gtag!('event', eventName, parameters);
  } catch (error) {
    console.error('[Analytics] Error sending event:', error);
  }
};

/**
 * Analytics instance with GA4 integration
 */
export const analytics = {
  /**
   * Track custom event
   */
  track: (event: string, properties?: Record<string, any>): void => {
    sendGAEvent(event, properties);
  },

  /**
   * Track page view (sends to both GA4 and backend)
   */
  page: (name: string, properties?: Record<string, any>): void => {
    const path = typeof window !== 'undefined' ? window.location.pathname : '/';

    // Send to backend analytics (fire-and-forget)
    sendPageViewToBackend(path, name);

    // Send to Google Analytics
    if (!isGAAvailable()) {
      console.log('[Analytics] Page view:', name, properties);
      return;
    }

    try {
      window.gtag!('event', 'page_view', {
        page_title: name,
        page_location: window.location.href,
        page_path: path,
        ...properties,
      });
    } catch (error) {
      console.error('[Analytics] Error tracking page view:', error);
    }
  },

  /**
   * Identify user (set user properties)
   */
  identify: (userId: string, traits?: Record<string, any>): void => {
    if (!isGAAvailable()) {
      console.log('[Analytics] Identify:', userId, traits);
      return;
    }

    try {
      window.gtag!('set', 'user_properties', {
        user_id: userId,
        ...traits,
      });

      // Also set as config
      window.gtag!('config', GA_MEASUREMENT_ID, {
        user_id: userId,
      });
    } catch (error) {
      console.error('[Analytics] Error identifying user:', error);
    }
  },

  /**
   * Reset user data (on logout)
   */
  reset: (): void => {
    if (!isGAAvailable()) {
      console.log('[Analytics] Reset user data');
      return;
    }

    try {
      window.gtag!('config', GA_MEASUREMENT_ID, {
        user_id: null,
      });
    } catch (error) {
      console.error('[Analytics] Error resetting user:', error);
    }
  },

  /**
   * Track custom event with parameters
   */
  trackEvent: (eventName: string, parameters?: Record<string, any>): void => {
    sendGAEvent(eventName, parameters);
  },

  /**
   * Track user action (category, action, label, value)
   */
  trackUserAction: (action: string, category: string, label?: string, value?: number): void => {
    sendGAEvent(action, {
      event_category: category,
      event_label: label,
      value: value,
    });
  },

  /**
   * Track engagement events
   */
  trackEngagement: (feature: string, action: string, details?: Record<string, any>): void => {
    sendGAEvent('engagement', {
      feature,
      action,
      ...details,
    });
  },

  /**
   * Track conversion events
   */
  trackConversion: (
    type: 'signup' | 'beta_request' | 'subscription' | 'meetup_created',
    value?: number,
    currency?: string
  ): void => {
    sendGAEvent('conversion', {
      conversion_type: type,
      value: value,
      currency: currency || 'USD',
    });

    // Also send as specific conversion event
    sendGAEvent(type, {
      value: value,
      currency: currency || 'USD',
    });
  },

  /**
   * Track errors for analytics
   */
  trackError: (message: string, context?: Record<string, any>): void => {
    sendGAEvent('error', {
      error_message: message,
      ...context,
    });
  },

  /**
   * Track performance metrics
   */
  trackPerformance: (name: string, value: number, unit?: string): void => {
    sendGAEvent('performance', {
      metric_name: name,
      metric_value: value,
      metric_unit: unit || 'ms',
    });
  },

  // Beta-specific tracking methods
  /**
   * Track beta signup
   */
  trackBetaSignup: (method: 'email' | 'google' | 'github', invitationCode?: string): void => {
    sendGAEvent('beta_signup', {
      method,
      invitation_code: invitationCode || 'BETA2025',
      signup_source: 'beta_launch',
    });

    // Also track as conversion
    analytics.trackConversion('signup');
  },

  /**
   * Track beta access request
   */
  trackBetaRequest: (_email: string): void => {
    sendGAEvent('beta_request', {
      request_source: 'beta_request_page',
    });

    analytics.trackConversion('beta_request');
  },

  /**
   * Track feedback submission
   */
  trackFeedbackSubmitted: (category: string, sentiment: 'positive' | 'negative' | 'neutral'): void => {
    sendGAEvent('feedback_submitted', {
      feedback_category: category,
      feedback_sentiment: sentiment,
    });
  },

  /**
   * Track meetup creation
   */
  trackMeetupCreated: (meetupType: string, participantCount: number): void => {
    sendGAEvent('meetup_created', {
      meetup_type: meetupType,
      participant_count: participantCount,
    });

    analytics.trackConversion('meetup_created');
  },

  /**
   * Track feature usage
   */
  trackFeatureUsage: (featureName: string, action: string): void => {
    sendGAEvent('feature_usage', {
      feature_name: featureName,
      feature_action: action,
    });
  },

  /**
   * Track onboarding progress
   */
  trackOnboardingStep: (step: number, stepName: string, completed: boolean): void => {
    sendGAEvent('onboarding_step', {
      step_number: step,
      step_name: stepName,
      completed: completed,
    });
  },

  /**
   * Track guest joining a meetup
   */
  trackGuestJoined: (meetupId: string, inviteMethod?: 'link' | 'email' | 'qr'): void => {
    sendGAEvent('guest_joined', {
      meetup_id: meetupId,
      invite_method: inviteMethod || 'link',
    });
  },

  /**
   * Track venue selection
   */
  trackVenueSelected: (meetupId: string, venueType: string, venueName?: string): void => {
    sendGAEvent('venue_selected', {
      meetup_id: meetupId,
      venue_type: venueType,
      venue_name: venueName,
    });
  },

  /**
   * Track upgrade/premium click
   */
  trackUpgradeClicked: (source: string, plan?: string): void => {
    sendGAEvent('upgrade_clicked', {
      click_source: source,
      target_plan: plan || 'premium',
    });
  },
};